import {
  faCheck,
  faChevronDown,
  faTimesCircle,
  faArrowUpRightFromSquare
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Combobox } from '@headlessui/react';
import _debounce from 'lodash/debounce';
import { type FC, useState, useMemo } from 'react';

import emptySelectOption from '@/constants/emptySelectOption';
import { OptionType } from '@/ui/components/Form/Select/types';

import { Props } from './types';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

// When a filtered list exceeds this size we cap the rendered count and show a hint.
// Rendering hundreds of DOM nodes at once causes the browser to freeze; a cap of 300
// keeps interaction smooth while the user types to narrow the list further.
// This value is intentionally above 252 (the countries-list library size) so that
// all existing country-selection dropdowns render their full option set.
const MAX_RENDERED_OPTIONS = 300;

/**
 * @deprecated Do not use this component. AutocompleteMultiSelect, AutocompleteSingleSelect,
 * and Combobox have been condensed into a single Select component. Use the one at the root
 * of the UI folder instead (src/ui/Select).
 */
const AutocompleteSingleSelect: FC<Props> = ({
  appendedText,
  buttonIcon = faChevronDown,
  clearable = true,
  disabled = false,
  error,
  formatOptionLabel,
  helpText,
  label,
  menuPlacement = 'bottom',
  missingOptionsText = 'No options',
  onChange,
  onSearch,
  options,
  placeholder = '',
  selectedOption,
  showLabel = true,
  debounce = true
}: Props) => {
  const [query, setQuery] = useState('');
  const filteredOptions = useMemo(
    () =>
      query === ''
        ? options
        : options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase())),
    [options, query]
  );

  // Cap the number of rendered options to avoid freezing on large lists.
  const renderedOptions = useMemo(
    () => filteredOptions.slice(0, MAX_RENDERED_OPTIONS),
    [filteredOptions]
  );
  const hiddenCount = filteredOptions.length - renderedOptions.length;

  const handleSetSelectedOptions = (option: OptionType | null) => {
    setQuery('');
    onChange((option as OptionType<string> | null) ?? emptySelectOption);
  };

  const debounceSearch = useMemo(() => {
    return _debounce((value: string) => onSearch?.(value), 500, {
      leading: false,
      trailing: true
    });
  }, [onSearch]);

  const handleSearch = (value: string) => {
    setQuery(value);

    if (debounce) {
      debounceSearch(value);
    } else {
      onSearch?.(value);
    }
  };

  const disabledClassNames = disabled ? 'opacity-50 cursor-not-allowed' : '';
  const showClear = clearable && !!selectedOption?.value;
  const showOutlink = !!selectedOption?.outlinkUrl;
  const chevronRight =
    showClear && showOutlink ? 'right-12' : showClear || showOutlink ? 'right-6' : 'right-0';

  return (
    <Combobox
      disabled={disabled}
      immediate
      onChange={handleSetSelectedOptions}
      value={selectedOption}>
      <>
        <Combobox.Label
          className={`block font-medium leading-6 text-gray-700 ${!showLabel ? 'hidden' : ''}`}>
          {label}
        </Combobox.Label>

        {helpText && <p className="text-sm text-gray-600">{helpText}</p>}

        <div className={`relative ${appendedText ? 'flex items-center' : ''}`}>
          <div
            className={`relative w-full cursor-default rounded-md bg-white text-left text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:outline-hidden focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6 ${disabledClassNames}`}>
            <Combobox.Input
              className={classNames(
                error ? 'bg-red-50 text-red-800 placeholder-red-500' : 'bg-white text-gray-900',
                `w-full rounded-md border-0 py-1.5 pl-3 pr-12 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`,
                disabledClassNames
              )}
              onChange={(event) => handleSearch(event.target.value)}
              placeholder={placeholder}
              displayValue={(option: OptionType | null) => {
                return option?.label ?? '';
              }}
            />

            <Combobox.Button
              className={`pointer-events-auto absolute inset-y-0 flex items-center pr-2 ${chevronRight}`}>
              <span className="sr-only">
                {(selectedOption && selectedOption.label) || placeholder}
              </span>
              <FontAwesomeIcon
                icon={buttonIcon}
                className="h-3 w-3 text-gray-500 "
                aria-hidden="true"
              />
            </Combobox.Button>
          </div>
          {showOutlink && (
            <a
              aria-label="Open in new tab"
              className={`text-gray-500 absolute inset-y-0 ${showClear ? 'right-8' : 'right-2'} top-1 ${disabledClassNames}`}
              href={selectedOption!.outlinkUrl}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              rel="noopener noreferrer"
              target="_blank"
              title="Open in new tab">
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-4 w-4" />
            </a>
          )}
          {clearable && selectedOption?.value && (
            <button
              aria-label="Clear"
              className={`text-gray-500 absolute inset-y-0 right-2 top-1 ${disabledClassNames}`}
              disabled={disabled}
              onClick={() => handleSetSelectedOptions(emptySelectOption)}
              title="Clear"
              type="button">
              <FontAwesomeIcon icon={faTimesCircle} className="h-4 w-4" />
            </button>
          )}

          <Combobox.Options
            className={`absolute z-10 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-hidden sm:text-sm ${menuPlacement === 'top' ? 'bottom-full mb-1' : 'mt-1'}`}>
            {filteredOptions.length === 0 ? (
              <Combobox.Option className="px-3 py-2" value={emptySelectOption}>
                <span className="text-gray-400">{missingOptionsText}</span>
              </Combobox.Option>
            ) : (
              // Return an array (not a Fragment) so headlessui's Frozen component doesn't
              // try to clone it with a ref — cloning a Fragment with ref throws in React 19.
              [
                ...renderedOptions.map((option) => (
                  <Combobox.Option
                    key={option?.value?.toString() ?? option.label}
                    className={({ active }) =>
                      classNames(
                        active ? 'bg-indigo-600 text-white' : '',
                        'w-full relative cursor-default select-none py-2 pl-8 pr-4'
                      )
                    }
                    value={option}>
                    {({ active, selected }) => (
                      <>
                        <div className="flex items-center">
                          {typeof option.avatar === 'string' ? (
                            <img
                              src={option.avatar}
                              alt=""
                              className="h-6 w-6 shrink-0 rounded-full"
                            />
                          ) : option.avatar === null ? (
                            <div className="h-6 w-6 shrink-0 rounded-full bg-gray-300" />
                          ) : null}
                          <span
                            className={classNames('ml-3 block', selected ? 'font-semibold' : '')}>
                            {formatOptionLabel ? formatOptionLabel(option) : option.label}
                          </span>
                          {option.subLabel && (
                            <span
                              className={classNames(
                                'ml-2 block text-sm',
                                active ? 'text-indigo-100' : 'text-gray-500'
                              )}>
                              - {option.subLabel}
                            </span>
                          )}
                          {option.outlinkUrl && (
                            <a
                              href={option.outlinkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={classNames(
                                'ml-auto mr-2',
                                active ? 'text-indigo-100' : 'text-gray-500'
                              )}
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              aria-label={`Open ${option.label} in new tab`}
                              title="Open in new tab">
                              <FontAwesomeIcon
                                icon={faArrowUpRightFromSquare}
                                className="h-3.5 w-3.5"
                              />
                            </a>
                          )}
                        </div>
                        {selected && (
                          <span
                            className={classNames(
                              'absolute inset-y-0 left-0 flex items-center pl-1.5',
                              active ? 'text-white' : 'text-indigo-600'
                            )}>
                            <FontAwesomeIcon
                              icon={faCheck}
                              className="h-3.5 w-3.5"
                              aria-hidden="true"
                            />
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                )),
                hiddenCount > 0 ? (
                  <div
                    key="__more_options"
                    className="px-3 py-2 text-sm text-gray-400 select-none pointer-events-none">
                    {hiddenCount} more — type to filter
                  </div>
                ) : null
              ]
            )}
          </Combobox.Options>

          {appendedText && <span className="ml-2">{appendedText}</span>}
        </div>
      </>
    </Combobox>
  );
};

export default AutocompleteSingleSelect;
